/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import * as React from 'react';
import type { AxiosError } from 'axios';
import axios from 'axios';
import {
  Box,
  createStyles,
  Grid,
  makeStyles,
  TextField,
  CircularProgress,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  Table,
  TableContainer,
  Paper,
  TableSortLabel,
  Button,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import useSWR from 'swr';
import { useSnackbar } from 'notistack';
import { format, parseISO } from 'date-fns';
import type { ICost, IRepairCost } from './RepairCostInterfaces';
import RepairCostDetailsCard from './RepairCostDetailsCard';
import RepairCostTotalCard from './RepairCostTotalCard';

const useStyles = makeStyles(() => createStyles({
  root: {
    flexGrow: 1,
  },
  estimatorCard: {
    height: 180,
  },
  estimatorSvg: {
    height: 24,
    width: 24,
    marginBottom: 7,
  },
  title: {
    fontWeight: 700,
    fontSize: 18,
  },
  estimatorRadioButtons: {
    '& .MuiFormGroup-root, .MuiFormControlLabel-root, label': {
      marginBottom: '0',
    },
  },
}));

const RepairCost: React.FC = () => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const [repairId, setRepairId] = React.useState<string>();
  const [loading, setLoading] = React.useState<boolean>(false);
  const [sortColumn, setSortColumn] = React.useState<keyof ICost>('scanTime');
  const [sortDirection, setSortDirection] = React.useState<'asc' | 'desc'>('asc');
  const [repairInfo, setRepairInfo] = React.useState<ICost[] | undefined>(undefined);

  const fetcher = async (url: string, currentRepairId: string) => {
    setLoading(true);
    const res = await axios.get<IRepairCost>(url, {
      params: {
        repairId: currentRepairId,
      },
    })
      .then((response) => response.data)
      .catch((e: AxiosError) => {
        if (e.response?.status === 404) {
          enqueueSnackbar(`There was the following error when fetching the repair: ${JSON.stringify(e.response.data)}`, { persist: false, variant: 'error' });
        } else {
          enqueueSnackbar(e?.response?.data, { persist: false, variant: 'error' });
        }
      })
      .finally(() => { setLoading(false); });
    return res;
  };

  const { data } = useSWR(repairId
    ? ['/repairCost/getCostsForRepair', repairId]
    : null,
  (url, repairIdParameter) => fetcher(url, repairIdParameter));

  function RequestRepairId() {
    return (
      <Grid container direction="column" spacing={2} alignItems="center" justifyContent="center">
        <Grid item>
          <Alert severity="info">{'Enter a repair id to view the cost of repair:'}</Alert>
        </Grid>
        <Grid item>
          {loading
            ? (
              <CircularProgress />
            )
            : (
              <TextField
                variant="outlined"
                size="small"
                onKeyPress={
                    (event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        event.stopPropagation();
                        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                        setRepairId((event.target as HTMLInputElement).value);
                      }
                    }
                }
              />
            )}
        </Grid>
      </Grid>
    );
  }

  React.useEffect(() => {
    setRepairInfo(data?.repairCosts);
  }, [data]);

  if (!repairInfo) {
    return (
      <RequestRepairId />
    );
  }

  const handleSort = (column: keyof ICost) => {
    const isAsc = sortColumn === column && sortDirection === 'asc';
    setSortDirection(isAsc ? 'desc' : 'asc');
    setSortColumn(column);
  };

  const sortedRows = [...repairInfo].sort((a, b) => {
    const aValue = a[sortColumn];
    const bValue = b[sortColumn];

    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    }
    return sortDirection === 'asc'
      ? String(aValue).localeCompare(String(bValue))
      : String(bValue).localeCompare(String(aValue));
  });

  return (
    <Box className={classes.root}>
      <Grid container justifyContent="center">
        <Grid item xs={12}>
          <Box display="flex" width="100%" justifyContent="right" marginBottom="0.5rem">
            <Button variant="contained" onClick={() => { setRepairId(undefined); setRepairInfo(undefined); }}>{'Reset'}</Button>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Box marginBottom="2rem">
            <Grid container spacing={3} justifyContent="center">
              <Grid item xs={12} sm={6} md={4} xl={3}>
                <RepairCostDetailsCard repairId={repairId ?? ''} />
              </Grid>
              <Grid item xs={12} sm={6} md={4} xl={3}>
                <RepairCostTotalCard cost={(repairInfo.map((x) => x.cost)).reduce((a, b) => a + b, 0)} />
              </Grid>
            </Grid>
          </Box>
        </Grid>
        <Grid item xs={12} md={8} xl={6}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={sortColumn === 'stationName'}
                      direction={sortColumn === 'stationName' ? sortDirection : 'asc'}
                      onClick={() => handleSort('stationName')}
                    >
                      {'Station Name'}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortColumn === 'scanTime'}
                      direction={sortColumn === 'scanTime' ? sortDirection : 'asc'}
                      onClick={() => handleSort('scanTime')}
                    >
                      {'Scan Time'}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortColumn === 'employeeFirstName'}
                      direction={sortColumn === 'employeeFirstName' ? sortDirection : 'asc'}
                      onClick={() => handleSort('employeeFirstName')}
                    >
                      {'Employee Name'}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={sortColumn === 'cost'}
                      direction={sortColumn === 'cost' ? sortDirection : 'asc'}
                      onClick={() => handleSort('cost')}
                    >
                      {'Cost'}
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedRows.map((item, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <TableRow key={item.employeeFirstName + item.employeeLastName + item.stationName + String(index)}>
                    <TableCell>{item.stationName}</TableCell>
                    <TableCell>{format(new Date(parseISO(item.scanTime)), 'MM/dd/yyyy HH:mm')}</TableCell>
                    <TableCell>{`${item.employeeFirstName} ${item.employeeLastName}`}</TableCell>
                    <TableCell align="right">
                      {'$'}
                      {item.cost.toFixed(2)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
    </Box>
  );
};

export default RepairCost;
